import { ref, computed } from 'vue';
import store from '@/store';
import { KhkAdminService } from '@/services/KhkAdminService';

// Create a singleton instance
const adminService = new KhkAdminService();

// Singleton cache for brand config fields (fetched once, shared across all useBrand() calls)
const _brandDomainName = ref('');
const _brandPlayMode = ref('');
let _domainFetchPromise = null;

/**
 * Composable to get the current brand information
 * @returns {Object} Brand information including code, name, and logo paths
 */
export function useBrand() {
    // Source of truth is the selected brand (#2122): the same store the switcher
    // and X-Brand-Code header read from. Deriving here keeps every useBrand()
    // consumer in lock-step with the active brand instead of a stale default.
    const brandCode = computed(() => store.state.selectedBrand?.code || 'giddyup');
    const brandName = computed(() => store.state.selectedBrand?.name || 'GiddyUp');

    const logoPath = computed(() => {
        return `/assets/brands/${brandCode.value}/logo.svg`;
    });

    const logoIconPath = computed(() => {
        return `/assets/brands/${brandCode.value}/logo-icon.svg`;
    });

    // Play mode arrives with the selected brand (GET/PUT /admin/me/brand include
    // play_mode), so it is known synchronously on every page load — no gating
    // flash. The lazily fetched brand-config value remains as a fallback for a
    // store hydrated before play_mode existed (#2122).
    const playMode = computed(() => store.state.selectedBrand?.playMode || _brandPlayMode.value);
    const isRealMoney = computed(() => playMode.value === 'real_money');
    const isSweepstakes = computed(() => playMode.value === 'sweepstakes');

    // Primary currency code for risk profile limit filtering
    const primaryCurrencyCode = computed(() => isRealMoney.value ? 'real-money' : 'gold-coin');

    /**
     * Lazily fetch and cache brand config fields from /admin/brand-config.
     * Returns the cached domain value if already fetched; deduplicates in-flight requests.
     */
    const fetchBrandDomain = async () => {
        if (_brandDomainName.value) return _brandDomainName.value;

        if (!_domainFetchPromise) {
            _domainFetchPromise = adminService.getBrandConfig()
                .then(config => {
                    _brandDomainName.value = config.domain_name || '';
                    _brandPlayMode.value = config.play_mode || '';
                    return _brandDomainName.value;
                })
                .catch(() => {
                    _domainFetchPromise = null;
                    return '';
                });
        }

        return _domainFetchPromise;
    };

    /**
     * Build the referral URL for a bonus code.
     * @param {string} code - The bonus code
     * @returns {string} Full referral URL, or empty string if domain unknown
     */
    const buildBonusCodeUrl = (code) => {
        const domain = _brandDomainName.value;
        if (!domain) return '';
        const base = domain.startsWith('http') ? domain : `https://${domain}`;
        return `${base}/?bonus_code=${encodeURIComponent(code)}`;
    };

    return {
        brandCode,
        brandName,
        brandDomainName: _brandDomainName,
        playMode,
        isRealMoney,
        isSweepstakes,
        primaryCurrencyCode,
        logoPath,
        logoIconPath,
        fetchBrandDomain,
        buildBonusCodeUrl
    };
}

